import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { StepperHeader } from './StepperHeader';
import { ArrowLeftIcon, Info } from 'lucide-react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import { PatientRequestProvider } from '../context/PatientRequestContext';
import { usePatientRequestContext } from '../context/usePatientRequestContext';
import { PATIENT_REQUEST_STEPS } from '../constants';
import { RequestLoading } from './request-steps/request/RequestLoading';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Separator,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@predoc/cow';
import { usePageTitle } from '@/hooks';

const PageContent = () => {
  const { currentStepIndex, isLoading } = usePatientRequestContext();
  const CurrentStepComponent =
    PATIENT_REQUEST_STEPS[currentStepIndex].component;

  if (isLoading) return <RequestLoading />;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      {CurrentStepComponent && <CurrentStepComponent />}
    </div>
  );
};

export const NewPatientRequestPage = () => {
  usePageTitle('New Patient Request');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);

  // Check if this is for an existing patient or edit mode
  const patientId = searchParams.get('patientId');
  const patientRequestId = searchParams.get('patientRequestId');
  const initialStep = patientId ? 1 : 0; // Start at step 2 (request) for existing patients

  // Creation flow should never persist step or substep params in the URL.
  useEffect(() => {
    if (patientRequestId) return;

    const hasStep = searchParams.has('step');
    const hasSubstep = searchParams.has('substep');

    if (!hasStep && !hasSubstep) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('step');
        next.delete('substep');
        return next;
      },
      { replace: true },
    );
  }, [patientRequestId, searchParams, setSearchParams]);

  const handleGoBack = () => {
    setIsDiscardDialogOpen(false);
    // If user came from patient profile, go back there; otherwise go to patients list
    const state = location.state as { fromPatientProfile?: boolean } | null;
    if (state?.fromPatientProfile && patientId) {
      navigate(`/patients/${patientId}`, { replace: true });
    } else {
      navigate('/patients', { replace: true });
    }
  };

  const handleDiscardDialogOpen = (isOpen: boolean) => {
    setIsDiscardDialogOpen(isOpen);
  };

  return (
    <PatientRequestProvider
      patientId={patientId || undefined}
      patientRequestId={patientRequestId || undefined}
      initialStep={initialStep}
    >
      <div className="min-h-screen bg-gray-50">
        <PageHeader>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleDiscardDialogOpen(true)}
              variant="ghost"
              size="icon"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold">New Patient Request</h1>
          </div>
          <StepperHeader />
        </PageHeader>
        <PageContent />
      </div>
      <Dialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <DialogContent className="p-0" aria-describedby={undefined}>
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Discard New Patient Request</DialogTitle>
          </DialogHeader>
          <Separator />
          <div className="p-4">
            <Alert>
              <Info className="size-5" />
              <AlertTitle>Confirm Discard New Patient Request</AlertTitle>
              <AlertDescription>All edits will be discarded.</AlertDescription>
            </Alert>
          </div>
          <Separator />

          <DialogFooter className="flex justify-end p-2 pt-0">
            <Button
              variant="outline"
              onClick={() => handleDiscardDialogOpen(false)}
            >
              Back to Editing
            </Button>
            <Button onClick={handleGoBack}>Discard</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PatientRequestProvider>
  );
};
